/**
 * DOM manipulation utilities
 */

/**
 * Create an element with attributes and children
 *
 * @param {string} tag - HTML tag name
 * @param {object} attrs - Attributes to set
 * @param {(string|Node)[]} children - Child elements or text
 * @returns {HTMLElement}
 */
export function createElement(tag, attrs = {}, children = []) {
  const el = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'className') {
      el.className = value;
    } else if (key === 'style' && typeof value === 'object') {
      Object.assign(el.style, value);
    } else if (key.startsWith('on') && typeof value === 'function') {
      const event = key.slice(2).toLowerCase();
      el.addEventListener(event, value);
    } else if (key === 'dataset') {
      Object.assign(el.dataset, value);
    } else {
      el.setAttribute(key, value);
    }
  }

  for (const child of children) {
    if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else if (child instanceof Node) {
      el.appendChild(child);
    }
  }

  return el;
}

/**
 * Shorthand for creating common elements
 */
export const el = {
  div: (attrs, children) => createElement('div', attrs, children),
  span: (attrs, children) => createElement('span', attrs, children),
  p: (attrs, children) => createElement('p', attrs, children),
  h1: (attrs, children) => createElement('h1', attrs, children),
  h2: (attrs, children) => createElement('h2', attrs, children),
  h3: (attrs, children) => createElement('h3', attrs, children),
  button: (attrs, children) => createElement('button', attrs, children),
  input: (attrs) => createElement('input', attrs),
  label: (attrs, children) => createElement('label', attrs, children),
  a: (attrs, children) => createElement('a', attrs, children),
  video: (attrs) => createElement('video', attrs)
};

/**
 * Clear all children from an element
 *
 * @param {HTMLElement} element
 */
export function clearElement(element) {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

/**
 * Replace element's children
 *
 * @param {HTMLElement} element
 * @param {(string|Node)[]} children
 */
export function setChildren(element, children) {
  clearElement(element);
  for (const child of children) {
    if (typeof child === 'string') {
      element.appendChild(document.createTextNode(child));
    } else if (child instanceof Node) {
      element.appendChild(child);
    }
  }
}

/**
 * Show an element (remove hidden class)
 *
 * @param {HTMLElement} element
 */
export function show(element) {
  element?.classList.remove('hidden');
}

/**
 * Hide an element (add hidden class)
 *
 * @param {HTMLElement} element
 */
export function hide(element) {
  element?.classList.add('hidden');
}

/**
 * Toggle element visibility
 *
 * @param {HTMLElement} element
 * @param {boolean} visible
 */
export function setVisible(element, visible) {
  if (visible) {
    show(element);
  } else {
    hide(element);
  }
}

/**
 * Set element's text content safely
 *
 * @param {HTMLElement} element
 * @param {string} text
 */
export function setText(element, text) {
  if (element) {
    element.textContent = text;
  }
}

/**
 * Query selector shorthand
 *
 * @param {string} selector
 * @param {Element} context
 * @returns {Element|null}
 */
export function $(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * Query selector all shorthand
 *
 * @param {string} selector
 * @param {Element} context
 * @returns {NodeList}
 */
export function $$(selector, context = document) {
  return context.querySelectorAll(selector);
}
